import type { Country } from '@/data';
import type { Answers, CountryMatchResult, Dimension, ScoringWeights } from './types';
import { deriveHouseholdBucket, weightedComponents, type HouseholdBucket } from './derive';
import { getHouseholdTotal, type CostOfLivingFile } from './cost-of-living';

type Lang = 'en' | 'ru';

export interface CountryNarrativeResult {
  /** 1-2 country-specific, answer-aware sentences for the result card. */
  sentences: string[];
  /** Which dimensions those sentences came from — lets the caller decide,
   *  e.g., whether to show the cost-figure disclaimer. */
  usedDimensions: Dimension[];
}

// Builds 1-2 country-specific, answer-aware narrative sentences for the result card.
// Returns plain strings (no i18n keys) — the copy is generated from metrics + answers.
// Guaranteed to return at least 1 snippet for any country with metric data.
export function buildCountryNarrative(
  country: Country,
  match: CountryMatchResult,
  answers: Answers,
  weights: ScoringWeights,
  lang: Lang,
  costFile?: CostOfLivingFile,
): CountryNarrativeResult {
  const m = country.metrics;
  if (!m) return { sentences: [], usedDimensions: [] };

  // Ranked by weighted contribution — strongest reasons first.
  // Lower threshold (0.40) to ensure coverage for countries ranked #2–5.
  const ranked = weightedComponents(match.components, weights)
    .filter((wc) => wc.component >= 0.40)
    .sort((a, b) => b.contribution - a.contribution);

  // User-stated priority → dimension mapping for "what you said matters" bonus.
  const priorityDims = new Set<Dimension>();
  const PRIORITY_MAP: Record<string, Dimension> = {
    safety: 'safety',
    healthcare: 'healthcare',
    schools: 'family',
    nature: 'climate',
    sports: 'climate',
    transport: 'jobs',
    'ru-community': 'banking',
  };
  for (const opt of answers.priorities?.optionIds ?? []) {
    const d = PRIORITY_MAP[opt];
    if (d) priorityDims.add(d);
  }

  const snippets: string[] = [];
  const used = new Set<Dimension>();

  function tryAdd(dim: Dimension): boolean {
    if (used.has(dim) || snippets.length >= 2) return false;
    const s = snippetFor(dim, m!, country, answers, lang, costFile);
    if (s) { snippets.push(s); used.add(dim); return true; }
    return false;
  }

  // 1st pass: user-stated priorities (these feel most personal)
  for (const dim of priorityDims) tryAdd(dim);

  // 2nd pass: top-ranked dims by algorithm
  for (const { dim } of ranked) tryAdd(dim);

  // Catch-all: if nothing generated, pick the top-ranked dim with any data at all
  if (snippets.length === 0 && ranked.length > 0) {
    for (const { dim } of ranked) {
      const s = snippetFallback(dim, m!, country, lang, answers, costFile);
      if (s) { snippets.push(s); used.add(dim); break; }
    }
  }

  return { sentences: snippets, usedDimensions: Array.from(used) };
}

// Household label — a short "for X" phrase naming who the cost figure
// applies to, in both languages. Fixed prepositional phrases (not built from
// a count + noun) to sidestep Russian case agreement. See
// deriveHouseholdBucket() for the mapping this is keyed against, including
// the documented lone-parent assumption.
const HOUSEHOLD_LABEL: Record<HouseholdBucket, { en: string; ru: string }> = {
  single: { en: 'for one person', ru: 'для одного человека' },
  couple: { en: 'for a couple', ru: 'для пары' },
  family_1: { en: 'for a family with one child', ru: 'для семьи с одним ребёнком' },
  family_2: { en: 'for a family with two children', ru: 'для семьи с двумя детьми' },
  family_3: { en: 'for a family with three or more children', ru: 'для семьи с тремя и более детьми' },
};

function snippetFor(
  dim: Dimension,
  m: NonNullable<Country['metrics']>,
  _country: Country,
  answers: Answers,
  lang: Lang,
  costFile?: CostOfLivingFile,
): string | null {
  const _ = lang === 'en';

  switch (dim) {
    case 'tax': {
      const rate = m.tax.flat_freelance_pct;
      if (rate != null && rate <= 15) {
        return _
          ? `Flat ${rate}% freelance tax — one of Europe's most competitive regimes for self-employed income.`
          : `Фиксированный ${rate}% налог для фрилансеров — один из лучших режимов Европы для самозанятых.`;
      }
      if (m.tax.pit_top_rate_pct <= 20) {
        return _
          ? `Top income tax at ${m.tax.pit_top_rate_pct}% — well below the EU average.`
          : `Максимальный подоходный налог ${m.tax.pit_top_rate_pct}% — значительно ниже среднего по ЕС.`;
      }
      const isRemote = answers.income?.optionIds.includes('remote') || answers.income?.optionIds.includes('passive');
      if (isRemote && m.tax.worldwide_taxation === false) {
        return _
          ? 'Territorial tax system — your foreign remote income is not taxed locally.'
          : 'Территориальная система налогообложения — иностранный доход не облагается налогом.';
      }
      return null;
    }

    case 'visa': {
      const passport = answers.passport?.optionIds[0];
      if (passport === 'eu' && m.visa.schengen) {
        return _
          ? 'Your EU passport gives free movement — no visa process, right to live and work immediately.'
          : 'Паспорт ЕС — свободное передвижение без виз, право жить и работать с первого дня.';
      }
      if (m.visa.digital_nomad) {
        const yr = m.visa.years_to_citizenship;
        const suffix = yr != null && yr <= 8
          ? (_ ? ` Citizenship path in ${yr} years.` : ` Путь к гражданству за ${yr} лет.`)
          : '';
        return _
          ? `Digital nomad visa available — a clear legal path to residency.${suffix}`
          : `Виза цифрового кочевника — чёткий путь к легальному резидентству.${suffix}`;
      }
      if (m.visa.years_to_citizenship != null && m.visa.years_to_citizenship <= 8) {
        return _
          ? `Citizenship in ${m.visa.years_to_citizenship} years — a clear naturalisation timeline.`
          : `Гражданство через ${m.visa.years_to_citizenship} лет — понятный путь натурализации.`;
      }
      return null;
    }

    case 'climate': {
      const cl = m.climate;
      const wanted = answers.climate?.optionIds ?? [];
      const types = cl.type as string[];

      if (wanted.includes('alps') && types.includes('alps')) {
        const cool = cl.jul_max_range?.[0] ?? cl.jul_max_c;
        return _
          ? `Alpine regions peak at ${cool}°C in July — cool summers with windows open at night.`
          : `Горные регионы прогреваются до ${cool}°C в июле — прохладное лето, ночи с открытым окном.`;
      }
      if (wanted.includes('mediterranean') && types.includes('mediterranean')) {
        return _
          ? `Mediterranean climate — ${cl.sunshine_h} sunshine hours per year, mild dry winters.`
          : `Средиземноморский климат — ${cl.sunshine_h} солнечных часов в год, мягкие сухие зимы.`;
      }
      if (wanted.includes('northern') && types.includes('northern')) {
        return _
          ? 'Northern climate — long summer evenings and clean air. Proper winter, proper summer.'
          : 'Северный климат — долгие летние вечера, чистый воздух. Настоящая зима и настоящее лето.';
      }
      if (wanted.includes('tropical') && types.includes('tropical')) {
        return _
          ? `Warm year-round — January min ${cl.jan_min_c}°C. No coat, no heating bills.`
          : `Тепло круглый год — минимум в январе ${cl.jan_min_c}°C. Без пальто и счетов за отопление.`;
      }
      if (wanted.includes('continental') && types.includes('continental')) {
        return _
          ? 'Four distinct seasons — proper winters, hot summers, crisp autumn.'
          : 'Четыре выраженных сезона — настоящая зима, жаркое лето, золотая осень.';
      }
      // Generic fallback: show actual climate data even when preference doesn't match exactly
      if (cl.sunshine_h) {
        return _
          ? `${cl.sunshine_h} sunshine hours per year — July peaks at ${cl.jul_max_c}°C.`
          : `${cl.sunshine_h} солнечных часов в год — пик в июле ${cl.jul_max_c}°C.`;
      }
      return null;
    }

    case 'cost': {
      // Name the household the figure belongs to — the old field was
      // printed as a bare number labelled "family", which misled every
      // reader who wasn't a family of ~4 (see
      // research/metrics-fabrication-audit.md). Uses the real per-household
      // total from cost_of_living.json when it's loaded and covers this
      // country; falls back to the legacy figure otherwise — but the
      // household label is always derived from the quiz answers, so even
      // the fallback sentence stops claiming to speak for "a family".
      const bucket = deriveHouseholdBucket(answers);
      const householdTotal = getHouseholdTotal(costFile, _country.code, bucket);
      const cost = householdTotal ?? m.cost.monthly_eur_family;
      const household = HOUSEHOLD_LABEL[bucket][lang];
      if (cost <= 1800) {
        return _
          ? `Monthly cost around €${cost} ${household} — one of the most affordable on your list.`
          : `Расходы около €${cost}/мес ${household} — один из самых доступных вариантов в вашем списке.`;
      }
      if (cost <= 2500) {
        return _
          ? `Living cost around €${cost}/month ${household} — mid-range for Western Europe.`
          : `Стоимость жизни около €${cost}/мес ${household} — средний уровень для Западной Европы.`;
      }
      if (cost <= 3200) {
        return _
          ? `Cost of living ~€${cost}/month ${household} — higher, but offset by infrastructure and quality of life.`
          : `Стоимость жизни ~€${cost}/мес ${household} — выше среднего, компенсируется инфраструктурой и качеством жизни.`;
      }
      return null;
    }

    case 'healthcare': {
      const uhc = m.healthcare.uhc_index;
      if (uhc >= 88) {
        return _
          ? `Universal healthcare — WHO quality index ${uhc}/100. You don't pay per appointment.`
          : `Всеобщее здравоохранение — индекс качества ВОЗ ${uhc}/100. Без платных приёмов.`;
      }
      if (uhc >= 75) {
        return _
          ? `Solid public healthcare (WHO index ${uhc}) — above OECD average.`
          : `Хорошая государственная медицина (ВОЗ ${uhc}) — выше среднего по ОЭСР.`;
      }
      if (uhc >= 60) {
        return _
          ? `Public healthcare available (WHO index ${uhc}) — basic coverage for residents.`
          : `Государственная медицина доступна (ВОЗ ${uhc}) — базовое покрытие для резидентов.`;
      }
      return null;
    }

    case 'safety': {
      const gpi = m.safety.gpi;
      if (gpi <= 1.4) {
        return _
          ? `Global Peace Index ${gpi} — one of the safest countries in the world.`
          : `Индекс глобального мира ${gpi} — одна из самых безопасных стран.`;
      }
      if (gpi <= 1.7) {
        return _
          ? `Low crime, stable institutions — Global Peace Index ${gpi}.`
          : `Низкая преступность, стабильные институты — Индекс мира ${gpi}.`;
      }
      if (gpi <= 2.1) {
        return _
          ? `Moderate safety profile — Global Peace Index ${gpi}. Comparable to most of Southern Europe.`
          : `Умеренный уровень безопасности — Индекс мира ${gpi}. Сопоставимо с большинством стран Южной Европы.`;
      }
      return null;
    }

    case 'language': {
      const langs = answers.languages?.optionIds ?? [];
      const official = new Set(m.language.official.map((l) => l.toLowerCase()));
      const LANG_NAMES: Record<string, { en: string; ru: string }> = {
        en: { en: 'English', ru: 'Английский' },
        it: { en: 'Italian', ru: 'Итальянский' },
        es: { en: 'Spanish', ru: 'Испанский' },
        de: { en: 'German', ru: 'Немецкий' },
        fr: { en: 'French', ru: 'Французский' },
        pt: { en: 'Portuguese', ru: 'Португальский' },
      };
      for (const l of langs) {
        if (l === 'any' || l === 'ru-only') continue;
        if (official.has(l)) {
          const n = LANG_NAMES[l];
          if (n) {
            return _
              ? `${n.en} is the official language — you'll integrate without a language barrier.`
              : `${n.ru} — официальный язык. Интеграция без языкового барьера.`;
          }
        }
      }
      if (m.language.english_band === 'very_high' || m.language.english_band === 'high') {
        return _
          ? 'English is widely spoken — everyday life works without the local language.'
          : 'Английский широко распространён — в быту обойдётесь без местного языка.';
      }
      if (m.language.english_band === 'moderate' && langs.includes('en')) {
        return _
          ? 'English understood in cities — workable without the local language in urban areas.'
          : 'Английский понимают в городах — в городской среде без местного языка можно обойтись.';
      }
      return null;
    }

    case 'banking': {
      const passport = answers.passport?.optionIds[0];
      if (passport === 'cis' && m.banking.ru_passport === 'yes') {
        return _
          ? 'Banking fully open for CIS passports — SWIFT transfers work, accounts accessible.'
          : 'Банки открыты для паспортов СНГ — SWIFT работает, счета открываются.';
      }
      if (passport === 'cis' && m.banking.ru_passport === 'limited') {
        return _
          ? 'Banking accessible with some restrictions for CIS passports — accounts are possible.'
          : 'Банкинг доступен с ограничениями для паспортов СНГ — счета открыть можно.';
      }
      if (passport === 'eu' || passport === 'anglo') {
        return _
          ? 'No banking friction with your passport — accounts open in days.'
          : 'С вашим паспортом нет банковских ограничений — счета открываются за несколько дней.';
      }
      return null;
    }

    case 'jobs': {
      const mbps = m.jobs.internet_mbps;
      const isRemote = answers.income?.optionIds.includes('remote');
      if (isRemote && mbps >= 100) {
        return _
          ? `Average ${mbps} Mbps broadband — reliable for remote work anywhere in the country.`
          : `Средняя скорость ${mbps} Мбит/с — надёжно для удалённой работы по всей стране.`;
      }
      if (isRemote && mbps >= 50) {
        return _
          ? `${mbps} Mbps average broadband — solid enough for remote work.`
          : `Средняя скорость ${mbps} Мбит/с — достаточно для удалённой работы.`;
      }
      return null;
    }

    case 'family': {
      const hasKids = answers.family?.optionIds.some((id) => id.startsWith('kids'));
      if (hasKids && m.family.english_schools) {
        return _
          ? 'English-language schools available — your children can continue in English while you settle.'
          : 'Есть англоязычные школы — дети продолжат учиться на английском, пока вы обустраиваетесь.';
      }
      if (hasKids) {
        const q = m.family.school_quality;
        if (q >= 0.75) {
          return _
            ? 'Strong education system — good school options for families.'
            : 'Сильная система образования — хорошие варианты школ для семей.';
        }
      }
      return null;
    }
  }
}

// Last-resort fallback: generate a simple sentence for any dimension with data.
// Called only when snippetFor() returned null for all ranked dimensions.
function snippetFallback(
  dim: Dimension,
  m: NonNullable<Country['metrics']>,
  _country: Country,
  lang: Lang,
  answers: Answers,
  costFile?: CostOfLivingFile,
): string | null {
  const _ = lang === 'en';
  switch (dim) {
    case 'tax':
      return _ ? 'Competitive tax environment for new residents.' : 'Конкурентная налоговая среда для новых резидентов.';
    case 'visa':
      return _ ? 'Legal residency path available — visa options confirmed.' : 'Путь к легальному резидентству — визовые варианты подтверждены.';
    case 'climate':
      return _ ? `July peak ${m.climate.jul_max_c}°C, ${m.climate.sunshine_h} sunshine hours/year.` : `Пик июля ${m.climate.jul_max_c}°C, ${m.climate.sunshine_h} солнечных часов в год.`;
    case 'cost': {
      const bucket = deriveHouseholdBucket(answers);
      const householdTotal = getHouseholdTotal(costFile, _country.code, bucket);
      const cost = householdTotal ?? m.cost.monthly_eur_family;
      const household = HOUSEHOLD_LABEL[bucket][lang];
      return _ ? `Living cost ~€${cost}/month ${household}.` : `Стоимость жизни ~€${cost}/мес ${household}.`;
    }
    case 'healthcare':
      return _ ? `Healthcare WHO index: ${m.healthcare.uhc_index}/100.` : `Индекс медицины ВОЗ: ${m.healthcare.uhc_index}/100.`;
    case 'safety':
      return _ ? `Global Peace Index ${m.safety.gpi} — stable country.` : `Индекс мира ${m.safety.gpi} — стабильная страна.`;
    case 'language':
      return _ ? 'English proficiency available in urban areas.' : 'Английский понимают в городской среде.';
    case 'banking':
      return _ ? 'Established banking system — account opening straightforward.' : 'Развитая банковская система — открытие счёта без сложностей.';
    case 'jobs':
      return _ ? `Internet: ${m.jobs.internet_mbps} Mbps average — workable for remote.` : `Интернет: ${m.jobs.internet_mbps} Мбит/с — подходит для удалённой работы.`;
    case 'family':
      return _ ? 'Family-friendly country — schools and healthcare available.' : 'Страна дружелюбна к семьям — школы и медицина доступны.';
  }
}
